// Recover from stale dynamic-import chunks after a deploy.
// React.lazy attaches its own rejection handler, so `unhandledrejection` does
// NOT fire for lazy-load failures — those land in ErrorBoundary instead, which
// calls maybeReloadForChunkError directly. The window listener below catches
// the remaining cases (e.g. ad-hoc `import()` calls outside React.lazy).

const RELOAD_KEY = "rb-chunk-reload-at"
const COOLDOWN_MS = 30_000
const PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed/

export function isChunkLoadError(err: unknown): boolean {
  const msg =
    err && typeof err === "object" && "message" in err
      ? String((err as { message: unknown }).message)
      : String(err)
  return PATTERN.test(msg)
}

// Returns true if a reload was scheduled (caller should suppress further error
// handling — page is about to tear down). Returns false if not a chunk error,
// or if the cooldown is still active (caller should fall back to normal error
// handling so the user has a visible recovery path).
export function maybeReloadForChunkError(err: unknown): boolean {
  if (typeof window === "undefined") return false
  if (!isChunkLoadError(err)) return false

  const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0
  if (Date.now() - last < COOLDOWN_MS) return false
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))

  void purgeAndReload()
  return true
}

async function purgeAndReload(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if ("caches" in window) {
      const names = await caches.keys()
      await Promise.all(names.map((n) => caches.delete(n)))
    }
  } catch {
    // best effort — proceed to reload regardless
  }
  window.location.reload()
}

if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    maybeReloadForChunkError(event.reason)
  })
}
